import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import {
  Customer,
  EnergyBalanceResponse,
  EnergyBalanceTimeBucketResponse,
  Invoice,
  PaginatedInvoiceList,
  EnergyBalanceParams,
  EnergyBalanceTimeBucketParams,
  ListInvoicesParams,
} from '../interfaces/metergrid';

/**
 * Energy API Service
 *
 * Alle Metergrid API-Calls laufen über das Python Backend (nicht direkt zu Metergrid).
 * Das Backend übernimmt:
 * - Authentifizierung mit Metergrid
 * - Credentials-Management
 * - Error Handling
 * - CORS-Handling
 *
 * API Endpunkte: /experience/energy/*
 */
@Injectable({
  providedIn: 'root',
})
export class EnergyApiService {
  private readonly baseUrl = `${environment.apiUrl}/experience/energy`;

  constructor(private http: HttpClient) { }

  // ========================================
  // Customer
  // ========================================

  /**
   * Get current user's energy customer data
   */
  async getMyCustomer(): Promise<Customer | null> {
    try {
      return await firstValueFrom(
        this.http.get<Customer>(`${this.baseUrl}/customer/me`)
      );
    } catch (error) {
      console.error('Error fetching customer data:', error);
      return null;
    }
  }

  /**
   * Get customer by ID
   */
  getCustomerById(customerId: number): Observable<Customer> {
    return this.http.get<Customer>(`${this.baseUrl}/customers/${customerId}`);
  }

  /**
   * Validate if a customer ID exists and is accessible
   * @param customerId - Customer ID to validate
   * @returns Promise resolving to true if valid, false otherwise
   */
  async validateCustomerId(customerId: number, regionalHubId?: number): Promise<{ valid: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.get<Customer>(`${this.baseUrl}/customers/${customerId}${regionalHubId ? `?regional_hub_id=${regionalHubId}` : ''}`)
      );
      return { valid: true };
    } catch (error: any) {
      console.error('Error validating customer ID:', error);

      if (error?.status === 404) {
        return { valid: false, error: 'Kundennummer nicht gefunden' };
      } else if (error?.status === 403) {
        return { valid: false, error: 'Keine Berechtigung für diese Kundennummer' };
      } else {
        return { valid: false, error: 'Fehler bei der Validierung' };
      }
    }
  }

  // ========================================
  // Energy Balance
  // ========================================

  /**
   * Get energy balance for a period
   * @param params - Query parameters (start, end, project_id)
   */
  async getEnergyBalance(
    params?: EnergyBalanceParams
  ): Promise<EnergyBalanceResponse | null> {
    try {
      const httpParams = this.buildHttpParams(params);
      return await firstValueFrom(
        this.http.get<EnergyBalanceResponse>(`${this.baseUrl}/energy-balance`, {
          params: httpParams,
        })
      );
    } catch (error) {
      console.error('Error fetching energy balance:', error);
      return null;
    }
  }

  /**
   * Get energy balance time series with configurable bucket size
   * @param params - Query parameters (start, end, bucket_size, project_id)
   */
  async getEnergyBalanceTimeBucket(
    params?: EnergyBalanceTimeBucketParams
  ): Promise<EnergyBalanceTimeBucketResponse | null> {
    try {
      const httpParams = this.buildHttpParams(params);
      return await firstValueFrom(
        this.http.get<EnergyBalanceTimeBucketResponse>(
          `${this.baseUrl}/energy-balance-time-bucket`,
          { params: httpParams }
        )
      );
    } catch (error) {
      console.error('Error fetching energy balance time bucket:', error);
      return null;
    }
  }

  // ========================================
  // Invoices
  // ========================================

  /**
   * List invoices for current user
   * @param params - Query parameters (page, page_size, state, period_start, period_end)
   */
  async listMyInvoices(
    params?: ListInvoicesParams
  ): Promise<PaginatedInvoiceList | null> {
    try {
      const httpParams = this.buildHttpParams(params);
      return await firstValueFrom(
        this.http.get<PaginatedInvoiceList>(`${this.baseUrl}/invoices`, {
          params: httpParams,
        })
      );
    } catch (error) {
      console.error('Error listing invoices:', error);
      return null;
    }
  }

  /**
   * Get invoice by ID
   */
  getInvoiceById(invoiceId: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.baseUrl}/invoices/${invoiceId}`);
  }

  /**
   * Download invoice PDF
   * @param invoiceId - Invoice ID
   * @returns Blob URL for download
   */
  async downloadInvoicePdf(invoiceId: number): Promise<string | null> {
    try {
      const blob = await firstValueFrom(
        this.http.get(`${this.baseUrl}/invoices/${invoiceId}/pdf`, {
          responseType: 'blob',
        })
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      return null;
    }
  }

  /**
   * Download invoice HTML
   */
  async downloadInvoiceHtml(invoiceId: number): Promise<string | null> {
    try {
      const html = await firstValueFrom(
        this.http.get(`${this.baseUrl}/invoices/${invoiceId}/html`, {
          responseType: 'text',
        })
      );
      return html;
    } catch (error) {
      console.error('Error downloading invoice HTML:', error);
      return null;
    }
  }

  // ========================================
  // Connection Management
  // ========================================

  /**
   * Connect current user to energy provider (Metergrid)
   * Enables energy features for this user
   */
  async connectEnergyProvider(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`${this.baseUrl}/connection/connect`, {})
      );
      return true;
    } catch (error) {
      console.error('Error connecting to energy provider:', error);
      return false;
    }
  }

  /**
   * Disconnect current user from energy provider
   * Disables energy features and stops data sync
   */
  async disconnectEnergyProvider(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`${this.baseUrl}/connection/disconnect`, {})
      );
      return true;
    } catch (error) {
      console.error('Error disconnecting from energy provider:', error);
      return false;
    }
  }

  /**
   * Get connection status for current user
   */
  async getConnectionStatus(): Promise<{
    connected: boolean;
    last_sync?: string;
    customer_id?: number;
  } | null> {
    try {
      return await firstValueFrom(
        this.http.get<{
          connected: boolean;
          last_sync?: string;
          customer_id?: number;
        }>(`${this.baseUrl}/connection/status`)
      );
    } catch (error) {
      console.error('Error fetching connection status:', error);
      return null;
    }
  }

  /**
   * Trigger manual sync of energy data
   */
  async syncEnergyData(): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`${this.baseUrl}/sync`, {}));
      return true;
    } catch (error) {
      console.error('Error syncing energy data:', error);
      return false;
    }
  }

  /**
   * Get latest meter reading for the user's measuring point
   */
  async getLatestMeterReading(): Promise<{
    id: number;
    timestamp: string;
    value: string;
    obis_code: string;
    measuring_point: string;
  } | null> {
    try {
      return await firstValueFrom(
        this.http.get<{
          id: number;
          timestamp: string;
          value: string;
          obis_code: string;
          measuring_point: string;
        }>(`${this.baseUrl}/latest-meter-reading`)
      );
    } catch (error) {
      console.error('Error fetching latest meter reading:', error);
      return null;
    }
  }

  /**
   * Get graph data for all measuring points of the user's panel
   * @param params - Query parameters (page_size)
   */
  async getGraphData(params?: {
    page_size?: number;
  }): Promise<Array<{
    value: number;
    measuring_point_id: number;
    id: number;
    obis_code: string;
    timestamp: string;
    reason: string;
    origin: string;
  }> | null> {
    try {
      const httpParams = this.buildHttpParams(params);
      return await firstValueFrom(
        this.http.get<Array<{
          value: number;
          measuring_point_id: number;
          id: number;
          obis_code: string;
          timestamp: string;
          reason: string;
          origin: string;
        }>>(`${this.baseUrl}/graph-data`, {
          params: httpParams,
        })
      );
    } catch (error) {
      console.error('Error fetching graph data:', error);
      return null;
    }
  }

  // ========================================
  // Utility Methods
  // ========================================

  /**
   * Build HttpParams from object
   */
  private buildHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Handle array parameters (e.g., id__in)
          httpParams = httpParams.append(key, value.join(','));
        } else {
          httpParams = httpParams.append(key, value.toString());
        }
      }
    });

    return httpParams;
  }
}
